interface EpisodeSortShape {
  field?: string;
  order?: string;
}

interface EpisodeFiltersShape {
  search?: string;
  source?: string | string[] | null;
  rating?: string | number | Array<string | number> | null;
  hostname?: string | string[] | null;
  collectorId?: string | string[] | null;
  hardwareVersion?: string | string[] | null;
  sopId?: string | string[] | null;
  tag?: string[] | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  reviewed?: boolean | boolean[] | null;
  hasInterventions?: boolean | boolean[] | null;
  subtaskCoverage?: string[] | null;
  viewMode?: string | null;
  sort?: EpisodeSortShape;
  pageSize?: number;
  currentPage?: number;
}

interface CollectionPresetsShape {
  task?: string;
  fps?: number;
  hardwareVersion?: string;
  sopLink?: string | null;
}

/**
 * Workflow filter snapshot. Mirrors the URL-encoded chip state on the ML
 * Studio workflows page. Sent to Gauss so the chat can read the current
 * filter state and decide whether to set new filters.
 */
interface WorkflowFiltersShape {
  status?: string | string[] | null;
  tag?: string | string[] | null;
  workflowName?: string | string[] | null;
}

/**
 * Where the chat is rendered. `sidebar` means the right-side AssistantPanel;
 * `main` means the full-page ChatPage. Tools that change app pages should
 * prefer a confirmation prompt when on `main` so the user isn't navigated
 * away from the active chat surface.
 */
export type ChatSurface = "sidebar" | "main";

export interface DashboardAssistantContextInput {
  dashboard_id: string;
  route_path: string;
  title: string;
  description: string | null;
  is_public: boolean;
  parameters: Array<{ id: string; type: string; label: string }>;
  parameter_values: Record<string, unknown>;
  resolved_sql_params: Record<string, unknown>;
  widgets: Array<{
    id: string;
    type: string;
    title: string;
    has_sql: boolean;
    position: number;
    output?: {
      loading: boolean;
      error_message: string | null;
      columns: string[];
      rows: Record<string, unknown>[];
      gated?: boolean;
      markdown?: string;
    };
  }>;
}

/** Snake_case payload sent to the assistant / chat API. */
export type AssistantAppStatePayload = {
  active_tab: string;
  workspace_path: string;
  episode_filters: {
    search: string;
    source: string | string[];
    rating: string | number | Array<string | number> | null;
    hostname: string | string[];
    collector_id: string | string[] | null;
    hardware_version: string | string[] | null;
    sop_id: string | string[] | null;
    tag: string[] | null;
    date_from: string | null;
    date_to: string | null;
    reviewed: boolean | boolean[] | null;
    has_interventions: boolean | boolean[] | null;
    subtask_coverage: string[] | null;
    view_mode: string;
    sort_by: string;
    sort_order: string;
    page_size: number;
    current_page: number;
  };
  collection_presets: {
    task: string;
    fps: number;
    hardware_version: string;
    sop_link: string;
  };
  workflow_filters: {
    status: string[] | null;
    tag: string[] | null;
    workflow_name: string[] | null;
  };
  chat_surface: ChatSurface;
  platform: string;
  dashboard_context?: {
    dashboard_id: string;
    route_path: string;
    title: string;
    description: string | null;
    is_public: boolean;
    parameters: DashboardAssistantContextInput["parameters"];
    parameter_values: Record<string, unknown>;
    resolved_sql_params: Record<string, unknown>;
    widgets: DashboardAssistantContextInput["widgets"];
  };
};

interface AssistantAppStateInput {
  activeTab: string;
  workspacePath?: string;
  platform: string;
  episodeFilters?: EpisodeFiltersShape;
  collectionPresets?: CollectionPresetsShape;
  /** Workflow filter snapshot. Omitted when not on the workflows page. */
  workflowFilters?: WorkflowFiltersShape;
  /** Where the chat is rendered. Defaults to "sidebar". */
  chatSurface?: ChatSurface;
  /** When set, Gauss receives dashboard-aware context and may use `query_dashboard_widget`. */
  dashboardContext?: DashboardAssistantContextInput | null;
}

function toStringArray(
  value: string | string[] | null | undefined,
): string[] | null {
  if (value == null) {
    return null;
  }
  const arr = Array.isArray(value) ? value : [value];
  const trimmed = arr
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter((v) => v.length > 0);
  return trimmed.length > 0 ? trimmed : null;
}

export function buildAssistantAppState({
  activeTab,
  workspacePath = "",
  platform,
  episodeFilters,
  collectionPresets,
  workflowFilters,
  chatSurface,
  dashboardContext,
}: AssistantAppStateInput): AssistantAppStatePayload {
  const base: AssistantAppStatePayload = {
    active_tab: activeTab,
    workspace_path: workspacePath,
    episode_filters: {
      search: episodeFilters?.search ?? "",
      source: episodeFilters?.source ?? "",
      rating: episodeFilters?.rating ?? null,
      hostname: episodeFilters?.hostname ?? "",
      collector_id: episodeFilters?.collectorId ?? null,
      hardware_version: episodeFilters?.hardwareVersion ?? null,
      sop_id: episodeFilters?.sopId ?? null,
      tag: episodeFilters?.tag ?? null,
      date_from: episodeFilters?.dateFrom ?? null,
      date_to: episodeFilters?.dateTo ?? null,
      reviewed: episodeFilters?.reviewed ?? null,
      has_interventions: episodeFilters?.hasInterventions ?? null,
      subtask_coverage: episodeFilters?.subtaskCoverage ?? null,
      view_mode: episodeFilters?.viewMode ?? "table",
      sort_by: episodeFilters?.sort?.field ?? "created_at",
      sort_order: episodeFilters?.sort?.order ?? "desc",
      page_size: episodeFilters?.pageSize ?? 0,
      current_page: episodeFilters?.currentPage ?? 1,
    },
    collection_presets: {
      task: collectionPresets?.task ?? "",
      fps: collectionPresets?.fps ?? 0,
      hardware_version: collectionPresets?.hardwareVersion ?? "",
      sop_link: collectionPresets?.sopLink ?? "",
    },
    workflow_filters: {
      status: toStringArray(workflowFilters?.status),
      tag: toStringArray(workflowFilters?.tag),
      workflow_name: toStringArray(workflowFilters?.workflowName),
    },
    chat_surface: chatSurface ?? "sidebar",
    platform,
  };

  if (dashboardContext) {
    return {
      ...base,
      dashboard_context: {
        dashboard_id: dashboardContext.dashboard_id,
        route_path: dashboardContext.route_path,
        title: dashboardContext.title,
        description: dashboardContext.description,
        is_public: dashboardContext.is_public,
        parameters: dashboardContext.parameters,
        parameter_values: dashboardContext.parameter_values,
        resolved_sql_params: dashboardContext.resolved_sql_params,
        widgets: dashboardContext.widgets,
      },
    };
  }

  return base;
}

export type {
  EpisodeFiltersShape,
  CollectionPresetsShape,
  WorkflowFiltersShape,
  AssistantAppStateInput,
};
